/**
 * Error codes are a CONTRACT between the API and every frontend.
 *
 * This union lives in the shared package, not in the API, so that a frontend
 * `switch` on `error.code` fails `pnpm typecheck` the moment a code is renamed
 * or removed. WSL's frontends switched on strings nobody could grep for.
 *
 * `apps/api/src/lib/errors.ts` re-exports this type — it is the single source.
 */
export type ErrorCode =
	| "VALIDATION_FAILED"
	| "INVALID_CREDENTIALS"
	| "ACCOUNT_INACTIVE"
	| "NO_ROLES_ASSIGNED"
	| "TOKEN_MISSING"
	| "TOKEN_INVALID"
	| "TOKEN_EXPIRED"
	| "SESSION_EXPIRED"
	| "SESSION_REVOKED"
	| "REFRESH_REUSE_DETECTED"
	| "ROLE_NOT_HELD"
	| "FORBIDDEN_ROLE"
	| "FORBIDDEN_SCOPE"
	/** 403: the login's user group does not hold the permission this action needs. */
	| "FORBIDDEN_PERMISSION"
	/** 403: the sport is not one of the login's user group's sports. */
	| "FORBIDDEN_SPORT"
	/** 422: a permission the group's role may not hold, or a login that does not hold the group's role. */
	| "GROUP_ROLE_MISMATCH"
	| "PASSWORD_CHANGE_REQUIRED"
	| "MFA_REQUIRED"
	| "MFA_ENROLLMENT_REQUIRED"
	| "MFA_INVALID_CODE"
	| "MFA_ALREADY_ENABLED"
	| "MFA_NOT_ENABLED"
	| "MFA_TICKET_INVALID"
	| "CANNOT_MANAGE_HIGHER_ROLE"
	| "CANNOT_GRANT_HIGHER_ROLE"
	/** A bulk action named the caller's own account (suspend / sign out yourself). */
	| "CANNOT_TARGET_SELF"
	| "CONFIRMATION_PASSWORD_INVALID"
	| "NOT_FOUND"
	| "CONFLICT"
	/** 422: an access set (a zone access rule, a card's own access) names an unknown or inactive access code, or more than one TRANSPORT code. */
	| "ACCESS_DEFAULTS_INVALID"
	/** 409: a zone access rule for this category / function / national federation already exists. Edit it instead. */
	| "ACCESS_RULE_EXISTS"
	/** 409: the cards this access rule change re-applies are not the number the preview showed (`details.expected_count`, `details.actual_count`). Preview again. */
	| "ACCESS_RULE_CARDS_CHANGED"
	/** Optimistic concurrency (409): someone else changed the record since this client loaded it. Reload, then retry. */
	| "STALE_VERSION"
	/** 409: a person with this ID type and number is already registered. `details.person` only for admins (K6). */
	| "ID_ALREADY_REGISTERED"
	/** 409: the card's status does not allow this action (the transition graph has no such edge), or the card changed state first. */
	| "INVALID_STATUS_TRANSITION"
	/** 422: the status override (`POST /accreditations/:id/status`) named the status the card already has. */
	| "STATUS_UNCHANGED"
	/** 403: registration for this games / category / organisation is not open now. Admins are never window-bound. */
	| "REGISTRATION_CLOSED"
	/** 403: the person who submitted a card cannot approve it. */
	| "SELF_APPROVAL"
	/** 409: a "select all N" bulk action's filter now matches a different number of records. Reload the list, then retry. */
	| "BULK_FILTER_CHANGED"
	/** 422: this kind of file does not accept that type, or the bytes are not what the type says (M4). */
	| "FILE_TYPE_NOT_ALLOWED"
	/** 422: the file is larger than allowed. `details.max_bytes`. */
	| "FILE_TOO_LARGE"
	/** 422: the image cannot be used. `details.reason` = too_small | aspect_ratio | unreadable; `min_width`, `min_height`. */
	| "FILE_IMAGE_INVALID"
	/** 422: the upload token is expired, tampered with, or minted for another login / role. Never 401: it must not sign anyone out. */
	| "UPLOAD_TOKEN_INVALID"
	/** 409: the upload never arrived in the bucket, or its size differs from the one declared. Upload again. */
	| "UPLOAD_INCOMPLETE"
	/** 409: the card already holds the most files of this kind. `details.max`. */
	| "FILE_LIMIT_REACHED"
	/** 409 (M9): this record's travel cannot be changed — it was rejected or withdrawn, or (for a register-only group) it is no longer editable. */
	| "TRAVEL_NOT_EDITABLE"
	/** 422 (transit, 0031): a leg's flights do not join up — a flight does not leave from the airport the previous one lands at. `details.direction`, `details.field` (the `connections` path), `details.position` (0-based index, in travel order, of the flight that does not join). */
	| "CONNECTION_ROUTE_MISMATCH"
	/** 422 (transit, 0031): a flight of the leg is scheduled to leave before the previous one is scheduled to land. Same `details` as CONNECTION_ROUTE_MISMATCH. */
	| "CONNECTION_TIME_ORDER"
	/** 422 (M10): the vehicles / drivers on a trip do not pass the check. `details.rows[{index, issues}]`, `details.totals` (TransportCheckResult); nothing was written. */
	| "ALLOCATION_INVALID"
	/** 409 (M10): another trip took this vehicle or driver at that time first (a race the check could not see). `details.kind` vehicle | driver, `id`, `ref`. */
	| "RESOURCE_BUSY"
	/** 422 (M10): a NOC trip must be booked at least `noc_lead_hours` ahead. `details.min_pickup_at`. */
	| "TRIP_LEAD_TIME"
	/** 409 (M10): a vehicle of this trip is on the road or done, so its vehicles and passengers can no longer change. */
	| "TRIP_LOCKED"
	/** 409 (M10): every leg of the transfer is already on a live transfer. `details.refs`. */
	| "TRANSFER_EXISTS"
	/** 409 (M10, 0034): another transport place already stands for this hotel (lodging building). `details.field` = lodging_building_id, `details.place_name`, `details.transport_place_id`. Unlink it there first. */
	| "HOTEL_ALREADY_LINKED"
	/** 409 (M11): another assignment holds this bed on an overlapping night (`ex_room_assignments_bed`). Pick another bed. */
	| "BED_TAKEN"
	/** 409 (M11): someone of the other sex stays in this room on an overlapping night (`ex_room_assignments_sex`). */
	| "ROOM_SEX_CONFLICT"
	/** 409 (M11): the card already holds an open assignment (`uq_room_assignments_open_card`) or a bed on those nights. Move instead. */
	| "ALREADY_HAS_ROOM"
	/** 409 (M11): the room cannot take this person. `details.reason` = reserved_other | out_of_service | sex | beds (ROOM_UNAVAILABLE_REASONS). */
	| "ROOM_NOT_AVAILABLE"
	/** 422 (M11): no travel, no contingent defaults and no games dates to derive a stay from — send `stay_from` / `stay_to`. */
	| "STAY_DATES_MISSING"
	/** 422 (M7a): PRIMARY would exceed the event's limit for the organisation (`shortlistPrimaryLimit`). `details.event_id`, `details.limit`, `details.used`. RESERVE is unlimited. */
	| "SHORTLIST_QUOTA_FULL"
	/** 409 (M7a): the organisation's shortlist for this sport is FINAL; only ADMIN / SUPER_ADMIN may change it until a SUPER_ADMIN reopens it. `details.sport_id`. */
	/** 409 (M7a): the person holds an OVR entry for this event, so they cannot be taken off its shortlist. Withdraw the entry first. `details.event_id`. */
	| "SHORTLIST_ENTERED"
	/** 422 (M7a): this card cannot be shortlisted for this event. `details.reason` = category | not_approved | sport | games (SHORTLIST_INELIGIBLE_REASONS). */
	| "SHORTLIST_NOT_ELIGIBLE"
	/** 422 (M7a, OVR): the person is not shortlisted (PRIMARY or RESERVE) for this event by their organisation. `details.people_ids`. */
	| "NOT_SHORTLISTED"
	/** 409 (OVR, 0079): another athlete already wears this bib in the sport. `details.holder` = their printed name, `details.sport_id`. */
	| "ATHLETE_BIB_TAKEN"
	/** 409 (M7a): the card's physical state does not allow this (no such CARD_STATE_TRANSITIONS edge, or it changed first). `details.card_state` when known. */
	| "CARD_STATE_INVALID"
	/** 409 (M7a): the incident can no longer be undone — the CARD_INCIDENT_UNDO_MINUTES window passed, it was already undone, or a later incident exists. */
	| "INCIDENT_UNDO_EXPIRED"
	/** 409 (M12): a lodging building already eats at another dining hall of the games (one hall per building per games). `details.lodging_building_id`, `details.dining_hall_id`, `details.dining_hall_name`. */
	| "BUILDING_HAS_HALL"
	/** 422 (M12): a dining hall and the buildings it serves must be of one cluster. `details.lodging_building_ids` (the ones of another cluster). */
	| "HALL_CLUSTER_MISMATCH"
	/** 422 (M12): the meal has started (games time zone), so a NOC can no longer order or cancel packed meals for it. `details.meal_starts_at`. */
	| "TAKEAWAY_MEAL_STARTED"
	/** 404 (M12): the scanned QR token or typed card number is not a card this login can see (unknown, voided, another games, out of scope). Nothing is logged. */
	| "CARD_NOT_FOUND"
	/** 409 (M7b): no entry window covers now for this games, phase and organisation (no row = closed). Scoped logins only; admins are never window-bound. `details.phase`. */
	| "ENTRY_WINDOW_CLOSED"
	/** 409 (M7b): the organisation's EOI / EBN is FINAL; only ADMIN / SUPER_ADMIN may change it until a SUPER_ADMIN reopens it. `details.phase`. */
	| "ENTRY_FINAL_LOCKED"
	/** 409 (M7b): the EBN opens once the organisation's EOI is FINAL. */
	| "EOI_NOT_FINAL"
	/** 422 (M7b): nothing to submit — an EOI needs at least one sport, an EBN at least one entry. `details.phase`. */
	| "ENTRY_EMPTY"
	/** 422 (M7b): an EBN number above the event's `max_entries_per_noc`. `details.event_id`, `details.cap`. */
	| "ENTRY_OVER_CAP"
	/** 422 (M7b): the sport is not one of the organisation's EOI sports. `details.sport_id`. */
	| "ENTRY_SPORT_NOT_IN_EOI"
	/** 422 (M7b): the event is not of this sport (or of this games). `details.event_ids`. */
	| "ENTRY_EVENT_NOT_IN_SPORT"
	/** 422 (0062): an EOI pick's expected entries above the event's `max_entries_per_noc`. `details.event_id`, `details.cap`, `details.events`. */
	| "ENTRY_EVENT_OVER_CAP"
	/** 422 (0062): an EOI pick's expected medals beyond `eventMedalCap`. `details.event_id`, `details.cap` ({ gold, silver, bronze, total }), `details.events`. */
	| "ENTRY_MEDALS_OVER_CAP"
	/** 403 (M7c): this part of the GMS is not open to NOCs at the login's stage yet. `details.module`, `details.opens_at_stage`, `details.stage`. ORGANIZATION sessions only. */
	| "STAGE_LOCKED"
	/** 409 (merch): a basket line asks for more than is left. `details.short` = `[{ variant_id, available }]`; nothing was written. */
	| "OUT_OF_STOCK"
	/** 409 (merch): store sign-up with an address that already has an account. No details. */
	| "SHOP_EMAIL_TAKEN"
	/** 409 (merch): the order is no longer waiting for payment (paid, cancelled, or its hold ran out). `details.status`. */
	| "ORDER_NOT_PAYABLE"
	/** 409 (merch): this sandbox payment attempt is already settled or expired. `details.status`. */
	| "PAYMENT_NOT_PENDING"
	/** 422 (merch): marking an order SHIPPED needs a courier and a tracking number (not a COLLECT order). */
	| "SHIPMENT_DETAILS_REQUIRED"
	/** 422 (merch): only a login that holds the ADMIN role can be put on the merch staff list. */
	| "MERCH_STAFF_NOT_ADMIN"
	/** 409 (merch): the catalogue holds SHOP_PRODUCTS_MAX products already (or the product its images / variants maximum). `details.max`. */
	| "PRODUCT_LIMIT_REACHED"
	/** 422 (merch): the promotion code cannot be used on this basket. `details.reason` (SHOP_PROMO_REFUSALS). */
	| "PROMO_NOT_APPLICABLE"
	/** 403 (TSR, 0058): a service token (display, paired screen, machine client) on a route that does not accept one. Not a verdict on the token: keep it. */
	| "SERVICE_TOKEN_NOT_ALLOWED"
	/** 403 (TSR, 0058): the service token's scope does not cover this. `details.reason`: read_only | sport | meet | station | kind. */
	| "FORBIDDEN_SERVICE_SCOPE"
	/** 422 (TSR, 0058): no pending pairing has this code (wrong, expired or already approved). Wrong codes count against the approver's limit. */
	| "PAIRING_CODE_INVALID"
	/** 404 (timing bench relay): no bench of that id has been heard from within TIMING_BENCH_TTL_SECONDS. */
	| "TIMING_BENCH_NOT_FOUND"
	/** 422 (timing bench relay): the bench has no lane that can be applied to this meet — `details.skipped` says why for each. */
	| "TIMING_BENCH_EMPTY"
	/** 409 (timing bench relay): the bench sent a newer reading than the one the operator applied (`captured_at`) — nothing written; look again. */
	| "TIMING_BENCH_CHANGED"
	/** 409 (timing bench relay): the timer says it timed another distance than this race's (`details.timer_distance_m`, `race_distance_m`) — nothing written. */
	| "TIMING_BENCH_RACE_MISMATCH"
	| "USERNAME_TAKEN"
	| "EMAIL_TAKEN"
	| "RATE_LIMITED"
	| "SERVICE_UNAVAILABLE"
	/**
	 * 501: this API is a verify-only copy (a venue laptop, JWT_JWKS_URL) and
	 * cannot sign tokens — sign-in, refresh, role switch, password change,
	 * device tokens and upload tokens happen at the cloud (`details.sign_at`,
	 * its origin). NOT a verdict on the credential: keep the tokens. Not
	 * transient either: retrying here never succeeds.
	 */
	| "SIGNING_DISABLED"
	/**
	 * 501: this server does not offer the feature at all — a venue computer
	 * (`API_PROFILE=venue`) has no file storage. Not transient: retrying here
	 * never succeeds; the feature lives at the cloud.
	 */
	| "NOT_AVAILABLE_HERE"
	| "INTERNAL_ERROR";

/**
 * ══════════════════════════════════════════════════════════════════════════
 * The one rule that must never be re-implemented per app
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Codes that are an actual VERDICT ON THE CREDENTIAL. Only these may cause a
 * client to discard its tokens and bounce the user to /login.
 *
 * Everything else — above all SERVICE_UNAVAILABLE — means "no answer", not
 * "no". WSL's mass logout was a Supabase timeout that every axios interceptor
 * read as a 401: tokens binned, 20k people signed out by a network blip.
 *
 * Deliberately NOT a status-code check. `status === 401` is the exact test that
 * caused the incident, because a dependency failure can surface with any status
 * a proxy feels like inventing. Ask the code, never the status.
 */
const CREDENTIAL_VERDICTS: ReadonlySet<string> = new Set<ErrorCode>([
	"TOKEN_MISSING",
	"TOKEN_INVALID",
	"SESSION_EXPIRED",
	"SESSION_REVOKED",
	"REFRESH_REUSE_DETECTED",
	"ACCOUNT_INACTIVE",
	// TOKEN_EXPIRED is absent on purpose: it is answered by refreshing, not by
	// signing the user out. See the client's refresh path.
]);

/** True only when the server has ruled on the credential itself. */
export const isCredentialVerdict = (code: string): boolean => CREDENTIAL_VERDICTS.has(code);

/** The dependency did not answer. KEEP the tokens, retry with backoff. */
export const isTransient = (code: string): boolean => code === "SERVICE_UNAVAILABLE";

/** An expired access token: refresh and retry, do not sign out. */
export const isRefreshable = (code: string): boolean => code === "TOKEN_EXPIRED";
